import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from 'primeng/api';
import { of } from 'rxjs';
import { catchError, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { PaymentService } from '@offconon/core-api';

import * as StripeCardPayActions from './stripe-card-pay.actions';

@Injectable()
export class StripeCardPayEffects {
  private actions$ = inject(Actions);
  private paymentService = inject(PaymentService);
  private messageService = inject(MessageService);

  processWalletPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StripeCardPayActions.walletPayment),
      map(({ token, addressBookId, amount, currency }) => ({
        token,
        addressBookId,
        amount: typeof amount === 'number' ? amount.toString() : amount,
        currency,
      })),
      switchMap(({ token, addressBookId, amount, currency }) =>
        this.paymentService
          .paymentWalletUploadIntentCreate({
            paymentMethodType: ['card'],
            amount: amount,
            currency: currency,
            address_book_id: addressBookId,
          })
          .pipe(
            switchMap(() =>
              this.paymentService
                .paymentChargeStripeCreate({
                  stripe_token: token.id,
                })
                .pipe(
                  map((chargeResult) => {
                    return StripeCardPayActions.confirmCardPaymentSuccess({
                      result: chargeResult,
                    });
                  }),
                  catchError((error) => {
                    return of(StripeCardPayActions.confirmCardPaymentFailure({ error }));
                  }),
                ),
            ),
          ),
      ),
    );
  });
  processSubscriptionPayment$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(StripeCardPayActions.subscriptionPayment),
      map(({ token, addressBookId }) => ({
        token,
        addressBookId,
      })),
      switchMap(({ token, addressBookId }) =>
        this.paymentService
          .paymentSubscriptionIntentCreate({
            paymentMethodType: ['card'],
            address_book_id: addressBookId,
          })
          .pipe(
            switchMap(() =>
              this.paymentService
                .paymentChargeStripeCreate({
                  stripe_token: token.id,
                })
                .pipe(
                  mergeMap((chargeResult) => {
                    return [
                      StripeCardPayActions.confirmCardPaymentSuccess({
                        result: chargeResult,
                      }),
                      StripeCardPayActions.confirmSubscriptionCardPaymentSuccess({
                        result: chargeResult,
                      }),
                    ];
                  }),
                  catchError((error) => {
                    return of(StripeCardPayActions.confirmCardPaymentFailure({ error }));
                  }),
                ),
            ),
            catchError((error) => {
              return of(StripeCardPayActions.confirmCardPaymentFailure({ error }));
            }),
          ),
      ),
    );
  });

  handlePaymentFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StripeCardPayActions.confirmCardPaymentFailure),
        tap(({ error }) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Payment Failed',

            detail:
              error?.message || 'An error occurred during payment processing. Please try again.',
          });
        }),
      ),
    { dispatch: false },
  );
}
